<template>
  <nav class="space-y-5 px-2" aria-label="Main">
    <div v-for="section in sections" :key="section.label" role="group" :aria-label="section.label">
      <p class="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
        {{ section.label }}
      </p>
      <ul class="space-y-0.5">
        <li v-for="link in section.links" :key="link.to">
          <router-link
            :to="link.to"
            class="nav-section-link"
            :class="{ 'nav-section-link-active': isActive(link) }"
            :aria-current="isActive(link) ? 'page' : undefined"
            @click="$emit('navigate')"
          >
            {{ link.label }}
          </router-link>
        </li>
      </ul>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router';
import type { NavLink, NavSection } from '@/types/nav';

/**
 * Renders the grouped nav. Used by the desktop sidebar and the mobile drawer, so the
 * markup (and the labels' styling) only exists once; the sections themselves come from
 * App.vue, which owns the role gating.
 */
defineProps<{ sections: NavSection[] }>();

// `navigate` lets the drawer close itself on a click; the sidebar has nothing to close.
defineEmits<{ navigate: [] }>();

const route = useRoute();

const isActive = (link: NavLink) => link.activeFor.includes(String(route.name));
</script>

<style scoped>
/* v4: a scoped block is its own compilation unit, so it must be pointed at the theme. */
@reference "../style.css";

.nav-section-link {
  @apply flex items-center rounded-md px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 hover:text-gray-900;
}

/* primary-100/800 are the pair the dark theme swaps (a deep indigo chip with light text),
   so one class works on both surfaces - unlike primary-50/700, which the dark block
   deliberately leaves alone because those carry the solid button. */
.nav-section-link-active {
  @apply bg-primary-100 text-primary-800;
}
</style>
